import { useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useAuth } from '../hooks/useAuth';
import { PageContainer } from '../components/PageContainer';
import { Button } from '../components/ui/Button';
import { EventForm } from '../components/EventForm';
import { UserRole } from '@wineryxplorer/shared';
import type { EventFormData, Event } from '@wineryxplorer/shared';

export const EventEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const formSubmitRef = useRef<(() => Promise<void>) | null>(null);

  // Fetch event data
  const { data: eventData, isLoading, error: fetchError } = trpc.event.getById.useQuery(
    { id: id || '' },
    { enabled: !!id }
  );

  // Type the event properly
  const event = eventData as Event | undefined;

  // Extract venueId early for venue query - handle both string and populated object
  let venueIdString: string | undefined;
  if (event?.venueId) {
    if (typeof event.venueId === 'string') {
      venueIdString = event.venueId;
    } else if (typeof event.venueId === 'object') {
      // Handle populated venue object
      const venueObj = event.venueId as unknown as { _id?: string; id?: string };
      venueIdString = venueObj._id || venueObj.id;
    }
  }

  // Fetch venue data to pre-fill location if event doesn't have one
  const { data: venueData } = trpc.venue.getById.useQuery(
    { id: venueIdString || '' },
    { enabled: !!venueIdString && !!event && !event.location }
  );

  // The venue management hub links here with `from=manage`, which is the only
  // signal that this edit began there rather than on the event's own page.
  const [searchParams] = useSearchParams();
  const cameFromManageHub = searchParams.get('from') === 'manage';

  // Back to wherever this edit started: the venue's management hub, or the
  // event's own page.
  const exitPath = () =>
    cameFromManageHub && venueIdString ? `/wineries/${venueIdString}/manage` : `/events/${id}`;

  // Update mutation
  const updateEvent = trpc.event.update.useMutation({
    onSuccess: () => {
      navigate(exitPath());
    },
    onError: (error) => {
      setError(error.message);
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  // Check if user has permission to edit
  const canEdit =
    user &&
    event &&
    (user.role === UserRole.SUPER_ADMIN ||
      user.role === UserRole.SITE_ADMIN ||
      user.id === event.organizerId);

  const handleSubmit = async (data: EventFormData) => {
    if (!id) return;
    
    setError(null);
    await updateEvent.mutateAsync({
      id,
      ...data,
    });
  };

  const handleSaveClick = async () => {
    if (formSubmitRef.current) {
      await formSubmitRef.current();
    }
  };

  const handleCancel = () => {
    navigate(exitPath());
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <div className="text-center py-8">
            <p className="text-gray-600">Loading event...</p>
          </div>
        </PageContainer>
      </div>
    );
  }

  // Error state
  if (fetchError || !event) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <div className="text-center py-8">
            <p className="text-red-600">
              {fetchError?.message || 'Event not found'}
            </p>
          </div>
        </PageContainer>
      </div>
    );
  }

  // Permission check
  if (!canEdit) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <div className="text-center py-8">
            <p className="text-red-600">
              You do not have permission to edit this event
            </p>
          </div>
        </PageContainer>
      </div>
    );
  }

  // Prepare initial data for form
  // Ensure venueId is a string (it might be populated as an object)
  const initialData: Partial<EventFormData> = {
    name: event.name,
    description: event.description,
    eventType: event.eventType,
    status: event.status,
    startDate: new Date(event.startDate).toISOString().split('T')[0], // Convert to YYYY-MM-DD
    startTime: event.startTime,
    duration: event.duration,
    venueId: venueIdString,
    maxAttendees: event.maxAttendees,
    price: event.price,
    requiresReservation: event.requiresReservation,
    registrationUrl: event.registrationUrl,
    audience: event.audience,
    tags: event.tags,
    contactInfo: event.contactInfo,
    // Use event location if it exists, otherwise pre-fill with venue address
    location: event.location || (venueData?.address
      ? {
          name: venueData.name,
          address: {
            street: venueData.address.street,
            city: venueData.address.city,
            state: venueData.address.state,
            zipCode: venueData.address.zipCode,
            country: venueData.address.country,
          },
        }
      : undefined),
    heroImageId: event.heroImageId,
    imageIds: event.imageIds,
    photoIds: event.photoIds,
    videoIds: event.videoIds,
  };

  return (
    <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
      <PageContainer className="mt-2">
        <div className="border-b border-gray-200 pb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Edit Event</h1>
          
          {/* Action Buttons - Top of Page */}
          <div className="flex gap-4 mb-6 justify-end">
            <Button
              variant="primary"
              onClick={handleSaveClick}
              disabled={updateEvent.isPending}
              loading={updateEvent.isPending}
            >
              {updateEvent.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <svg
                className="w-5 h-5 text-red-400 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">
                  Error updating event
                </h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Event Form */}
        <div className="mt-6">
          <EventForm
            initialData={initialData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={updateEvent.isPending}
            hideButtons
            submitRef={formSubmitRef}
          />
        </div>
      </PageContainer>
    </div>
  );
};
